/**
 * Core-100 CORE-P (owner direction 4) — how does work go when two technicians share it?
 *
 * Input: the evidence aggregates from `team_compatibility_evidence` (one shop, one period). Output: one
 * assessment per PAIR. The pair — the working combination — is the subject; nothing here describes,
 * scores or ranks a person (constitution: "A and B move work slowly together" MUST NOT become "A is slow").
 *
 * Method (explainable on purpose):
 *   r = ln(actual labour minutes / book minutes) per completed job.
 *   pair mean  mP over the pair's shared jobs (nP jobs, sd sP);
 *   baseline   mB = (mA + mB') / 2 — the average of each member's SOLO jobs at the same shop and period;
 *   effect     e = mP − mB, reported as a percentage of time: exp(e) − 1;
 *   uncertainty SE = sqrt(sP²/nP + (sA²/nA + sB²/nB) / 4); 95% range e ± 1.96·SE.
 * Gates:
 *   nP < 10 shared jobs                     → 'insufficient_evidence' (no effect is shown, only the count);
 *   either member < 3 solo jobs (no baseline) → 'no_baseline';
 *   the 95% range includes 0                → 'no_clear_difference';
 *   otherwise 'more_time_together' / 'less_time_together', with confidence moderate (nP < 20) or high.
 * A missing sd (a single job) is treated as unknown spread and the comparison is not made.
 * Nothing here is used for assignment; it informs a manager.
 */

export const MIN_SHARED_JOBS = 10;

/**
 * Core-100 R4 wave 2 CORE-P (time boundary): the period is N SHOP-LOCAL calendar days, resolved by the server
 * (`team_compatibility_evidence_recent`: today in the shop's zone and the N-1 days before it, midnight to midnight).
 * The browser no longer subtracts N*86 400 000 ms from its own clock, which put the edge at the viewer's wall-clock
 * hour on day -N in the viewer's zone.
 */
export const TEAM_COMPATIBILITY_PERIOD_RPC = 'team_compatibility_evidence_recent' as const;

export function teamCompatibilityPeriodArgs(shopId: string, days: number | string): { p_shop_id: string; p_days: number } {
  const n = typeof days === 'string' ? Number(days) : days;
  if (!Number.isInteger(n) || n < 1 || n > 3660) throw new Error(`TEAM_COMPATIBILITY_PERIOD_REQUIRED: ${String(days)}`);
  return { p_shop_id: shopId, p_days: n };
}

/** "2026-07-15 to 2026-10-12 (shop time)" from the server-stated shop-local days, or null when none was stated. */
export function describeTeamPeriod(rows: ReadonlyArray<{ period_first_day?: string | null; period_last_day?: string | null }>): string | null {
  const r = rows.find((x) => x.period_first_day && x.period_last_day);
  return r ? `${r.period_first_day} to ${r.period_last_day} (shop time)` : null;
}
export const MIN_SOLO_JOBS = 3;

export interface TeamEvidenceRow {
  kind: 'pair' | 'solo';
  technician_a: string;
  technician_b: string | null;
  job_count: number;
  mean_log_ratio: number | null;
  sd_log_ratio: number | null;
  actual_minutes: number;
  book_minutes: number;
  /** shop-local first/last day of the period (team_compatibility_evidence_recent) */
  period_first_day?: string | null;
  period_last_day?: string | null;
}

export type TeamCompatibilityStatus =
  | 'insufficient_evidence'
  | 'no_baseline'
  | 'no_clear_difference'
  | 'more_time_together'
  | 'less_time_together';

export interface TeamCompatibilityAssessment {
  technicianA: string;
  technicianB: string;
  sharedJobs: number;
  status: TeamCompatibilityStatus;
  /** % more (+) or less (−) time than the members' solo baseline; null unless a comparison was made. */
  effectPct: number | null;
  rangeLowPct: number | null;
  rangeHighPct: number | null;
  confidence: 'none' | 'low' | 'moderate' | 'high';
  /** One paragraph naming the evidence. Uses the pair's names when given. */
  explanation: string;
}

const pct = (logDelta: number) => Math.round((Math.exp(logDelta) - 1) * 100);

export function assessTeamCompatibility(
  rows: readonly TeamEvidenceRow[],
  names: Record<string, string> = {},
): TeamCompatibilityAssessment[] {
  const solo = new Map(rows.filter((r) => r.kind === 'solo').map((r) => [r.technician_a, r]));
  const out: TeamCompatibilityAssessment[] = [];
  for (const p of rows) {
    if (p.kind !== 'pair' || !p.technician_b) continue;
    const a = names[p.technician_a] ?? 'these two technicians';
    const b = names[p.technician_b] ?? '';
    const who = b ? `${a} and ${b}` : a;
    const base = { technicianA: p.technician_a, technicianB: p.technician_b, sharedJobs: p.job_count };

    if (p.job_count < MIN_SHARED_JOBS) {
      out.push({ ...base, status: 'insufficient_evidence', effectPct: null, rangeLowPct: null, rangeHighPct: null, confidence: 'none',
        explanation: `${who} have shared ${p.job_count} completed job${p.job_count === 1 ? '' : 's'} in this period. At least ${MIN_SHARED_JOBS} are needed before OptiBay says anything about how their shared work goes.` });
      continue;
    }
    const sa = solo.get(p.technician_a);
    const sb = solo.get(p.technician_b);
    if (!sa || !sb || sa.job_count < MIN_SOLO_JOBS || sb.job_count < MIN_SOLO_JOBS
        || sa.mean_log_ratio == null || sb.mean_log_ratio == null || p.mean_log_ratio == null
        || p.sd_log_ratio == null || sa.sd_log_ratio == null || sb.sd_log_ratio == null) {
      out.push({ ...base, status: 'no_baseline', effectPct: null, rangeLowPct: null, rangeHighPct: null, confidence: 'none',
        explanation: `${who} shared ${p.job_count} completed jobs, but there is not enough solo work from each of them in this period to compare against, so no difference is reported.` });
      continue;
    }
    const baseline = (sa.mean_log_ratio + sb.mean_log_ratio) / 2;
    const effect = p.mean_log_ratio - baseline;
    const se = Math.sqrt(p.sd_log_ratio ** 2 / p.job_count + (sa.sd_log_ratio ** 2 / sa.job_count + sb.sd_log_ratio ** 2 / sb.job_count) / 4);
    const lo = effect - 1.96 * se;
    const hi = effect + 1.96 * se;
    const effectPct = pct(effect);
    const rangeLowPct = pct(lo);
    const rangeHighPct = pct(hi);
    const range = `95% range ${rangeLowPct >= 0 ? '+' : ''}${rangeLowPct}% to ${rangeHighPct >= 0 ? '+' : ''}${rangeHighPct}%`;
    const caveat = 'This describes how shared work between them goes — not either person — and is not a reason on its own to change anyone\'s assignments.';
    if (lo <= 0 && hi >= 0) {
      out.push({ ...base, status: 'no_clear_difference', effectPct, rangeLowPct, rangeHighPct, confidence: 'low',
        explanation: `On ${p.job_count} shared jobs, ${who} took about as long as their solo work would suggest (${range}). ${caveat}` });
      continue;
    }
    const more = effect > 0;
    out.push({ ...base, status: more ? 'more_time_together' : 'less_time_together', effectPct, rangeLowPct, rangeHighPct,
      confidence: p.job_count >= 20 ? 'high' : 'moderate',
      explanation: `On ${p.job_count} shared jobs, work took ${Math.abs(effectPct)}% ${more ? 'more' : 'less'} time than book compared with what each of them takes working alone (${range}; ${sa.job_count} and ${sb.job_count} solo jobs as the baseline). ${caveat}` });
  }
  // Ordered by evidence (most shared jobs first) — deliberately NOT by effect, so the list is not a ranking.
  return out.sort((x, y) => y.sharedJobs - x.sharedJobs || x.technicianA.localeCompare(y.technicianA));
}
